import React, { FC, Fragment } from 'react';
import {
    Alert,
    Box,
    Button,
    DialogActions,
    DialogContent,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { getRolesTemplate } from 'apollo/templates/user/query.graphql';
import { linkUserToOrganizationTemplate } from 'apollo/templates/user/mutation.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { OrgDropdown, Role, User } from 'models/user';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import Loader from 'components/shared/loader/loader.component';
import { LoadingButton } from '@mui/lab';
import { Organization } from 'models/organization';

export type LinkedUser = {
    organization: string;
    role: string;
    active: boolean;
    type: string;
    isWhiteLabeled: boolean;
};

type LinkUserFormComponentProps = {
    user: User;
    /** The organization the user is linked into (super-admin-backend requires it). */
    organization: OrgDropdown;
    userType: string;
    onBack: () => void;
    onUserLinked: (user: User) => void;
};
const LinkUserFormComponent: FC<LinkUserFormComponentProps> = ({
    user,
    organization,
    userType,
    onBack,
    onUserLinked,
}: LinkUserFormComponentProps) => {
    const { t: translate } = useTranslation(['common', 'users', 'organization', 'super-admin']);
    const { showBoundary } = useDenthubErrorBoundary();

    const [linkUserToOrganization, { loading }] = useMutation(linkUserToOrganizationTemplate, {
        onCompleted: ({ linkUserToOrganization: user }) => {
            onUserLinked(user);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    const { data: { roles } = {}, loading: rolesLoading } = useQuery(getRolesTemplate, {
        notifyOnNetworkStatusChange: true,
        variables: { type: userType },
        onError(error) {
            showBoundary(error);
        },
    });

    const validate = (values: LinkedUser) => {
        const errors: Record<string, string> = {};
        if (!values.role) {
            errors.role = translate('users:VALIDATION:ROLE');
        }
        return errors;
    };
    const formik = useFormik({
        initialValues: {
            organization: organization._id,
            role: '',
            type: userType === 'DENTIST' ? 'dentist' : 'lab_technician',
            active: true,
            isWhiteLabeled: (user.organization as Organization)?.isWhiteLabeled || false,
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: async (values: LinkedUser) => {
            await linkUserToOrganization({
                variables: {
                    userId: user._id,
                    linkDetails: values,
                },
            });
        },
    });

    return (
        <form onSubmit={formik.handleSubmit}>
            <DialogContent>
                <Box>
                    {rolesLoading ? (
                        <Loader />
                    ) : (
                        <Fragment>
                            <Box mb={2}>
                                <Typography variant="body1">
                                    {translate('super-admin:LINK_USER_INFO', { organization: organization.name })}
                                </Typography>
                            </Box>
                            <Box mb={2}>
                                <Box>
                                    <Typography variant="subtitle2">{translate('NAME')}</Typography>
                                    <Box>{user.fullName}</Box>
                                </Box>
                                <Box>
                                    <Typography variant="subtitle2">{translate('EMAIL')}</Typography>
                                    <Box>{user.email}</Box>
                                </Box>
                            </Box>

                            <FormControl
                                margin="dense"
                                fullWidth
                                error={formik.touched.role && Boolean(formik.errors.role)}
                                required
                            >
                                <InputLabel id="role-select-label">{translate('common:ROLE')}</InputLabel>
                                <Select
                                    labelId="role-select-label"
                                    id="role-select"
                                    label={translate('common:ROLE')}
                                    required
                                    error={formik.touched.role && Boolean(formik.errors.role)}
                                    {...formik.getFieldProps('role')}
                                >
                                    {roles.map((role: Role) => (
                                        <MenuItem key={role._id} value={role.role}>
                                            {translate(`common:${role.role}`)}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText error={formik.touched.role && Boolean(formik.errors.role)}>
                                    {formik.touched.role && formik.errors.role}
                                </FormHelperText>
                            </FormControl>

                            {formik.values.role ? (
                                <Box mt={1}>
                                    <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {translate(`users:${formik.values.role}_DESCRIPTION`)}
                                    </Alert>
                                </Box>
                            ) : undefined}
                        </Fragment>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onBack}>{translate('common:BACK')}</Button>
                <LoadingButton loading={loading} disabled={!formik.isValid} variant="contained" type="submit">
                    {translate('common:LINK')}
                </LoadingButton>
            </DialogActions>
        </form>
    );
};

export default LinkUserFormComponent;
